import { IndianRupee } from 'lucide-react';

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
};

export const formatDate = (date: string | Date): string => {
  return new Intl.DateTimeFormat('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(date));
};

export const getStatusColor = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'paid':
      return 'bg-success-100 text-success-700 border-success-200';
    case 'overdue':
      return 'bg-error-100 text-error-700 border-error-200';
    case 'sent':
      return 'bg-warning-100 text-warning-700 border-warning-200';
    case 'draft':
      return 'bg-gray-100 text-gray-700 border-gray-200';
    case 'approved':
      return 'bg-success-100 text-success-700 border-success-200';
    case 'rejected':
      return 'bg-error-100 text-error-700 border-error-200';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200';
  }
};

export const generateInvoiceNumber = (): string => {
  const date = new Date();
  const year = date.getFullYear().toString().substr(-2);
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  return `INV-${year}${month}-${random}`;
};

export const generateQuoteNumber = (): string => {
  const date = new Date();
  const year = date.getFullYear().toString().substr(-2);
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  return `QUO-${year}${month}-${random}`;
};

export const calculateDaysOverdue = (dueDate: string): number => {
  const due = new Date(dueDate);
  const today = new Date();
  const diffTime = today.getTime() - due.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > 0 ? diffDays : 0;
};

export const downloadPDF = async (filename: string, content: string): Promise<void> => {
  try {
    // Create a blob with the content
    const blob = new Blob([`PDF Content for ${filename}\n\n${content}`], { type: 'application/pdf' });
    
    // Create a URL for the blob
    const url = URL.createObjectURL(blob);
    
    // Create a link element
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.pdf`;
    
    // Append to the document, click it, and remove it
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 100);
  } catch (error) {
    console.error('Error downloading PDF:', error);
    throw error;
  }
};

export const sendEmail = async (email: string, subject: string, body: string): Promise<void> => {
  try {
    console.log(`Sending email to ${email}`);
    console.log(`Subject: ${subject}`);
    console.log(`Body: ${body}`);
    
    // In a real app, this would call an API endpoint to send the email
    // For demo purposes, we'll simulate a successful email send
    
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Success! Email sent
    console.log('Email sent successfully');
  } catch (error) {
    console.error('Error sending email:', error);
    throw error;
  }
};

export const formatPhoneNumber = (phone: string): string => {
  // Format phone number for display
  if (!phone) return '';
  
  // Simple formatting for Indian numbers
  if (phone.startsWith('+91')) {
    return phone.replace(/(\+91)(\d{5})(\d{5})/, '$1-$2-$3');
  }
  
  return phone;
};

// Generate a PDF for an invoice
export const generateInvoicePDF = async (invoice: any): Promise<void> => {
  return downloadPDF(`Invoice_${invoice.invoice_number}`, JSON.stringify(invoice));
};

// Generate a PDF for a quote
export const generateQuotePDF = async (quote: any): Promise<void> => {
  return downloadPDF(`Quote_${quote.quote_number}`, JSON.stringify(quote));
};

// Generate a PDF receipt for a payment
export const generatePaymentReceipt = async (payment: any): Promise<void> => {
  return downloadPDF(`Payment_Receipt_${payment.id.slice(-8)}`, JSON.stringify(payment));
};

// Generate a PDF report for an expense
export const generateExpenseReport = async (expense: any): Promise<void> => {
  return downloadPDF(`Expense_Report_${expense.id.slice(-8)}`, JSON.stringify(expense));
};